import { useCallback, useMemo } from "react"

import { useMatch, useParams } from "react-router-dom"

import { useImageCollectionDispatchContext } from "@l2r-front/l2r-images"
import {
    Layer,
    REFERENTIAL_LAYER,
    useOrderingLayers,
    useMapDispatchContext,
    DOCUMENTS_LAYER,
    DOCUMENTS_SOURCE,
} from "@l2r-front/l2r-map"
import { useNetworksDispatchContext } from "@l2r-front/l2r-networks"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { useNavigateWithSearchParams, useUrlParams } from "@l2r-front/l2r-utils"

import { APP_DOCUMENT, MODULE_DOCUMENTS, ModulesInfo, NETWORK_ATTACHMENT } from "../../constants/modules"
import { LINEAR_LOCATION_END, LINEAR_LOCATION_START, TYPE_ID } from "../../constants/urlParams"
import { useDocumentStateContext } from "../../contexts/DocumentContext"
import { DEFAULT_INDEX, getDocumentItemLayerStyle, NETWORK_ICON_INDEX } from "../../utils/getDocumentsLayerConfig"

import { DocumentsSource } from "../DocumentsSource"

export const ICON_SIZE = 60
export const COLOR_SIZE = 20

export function DocumentsLayer() {

    const params = useParams()
    const { setSelectedLinearLocations } = useNetworksDispatchContext()
    const { setImageFeatureClickedPosition, setSegmentFeatureIdClicked } = useImageCollectionDispatchContext()
    const road = params?.road
    const attachmentId = params?.attachmentId
    const { getParam } = useUrlParams()
    const matchNetworkDetail = useMatch(`:slugId/${APP_DOCUMENT}/${MODULE_DOCUMENTS}/attached/${NETWORK_ATTACHMENT}`)

    const linearLocationStart = getParam(LINEAR_LOCATION_START)
    const linearLocationEnd = getParam(LINEAR_LOCATION_END)
    const { storeMapBoundingBox } = useMapDispatchContext()
    const { hoveredAttachedObject } = useDocumentStateContext()
    useOrderingLayers(["road-number", "road-number-shield", DOCUMENTS_LAYER])

    const navigateWithSearchParams = useNavigateWithSearchParams()

    const catalogItemsLayerConfig = useMemo(() => {
        return getDocumentItemLayerStyle(attachmentId, road, linearLocationStart, linearLocationEnd, hoveredAttachedObject)
    }, [attachmentId, linearLocationEnd, linearLocationStart, road, hoveredAttachedObject])

    const handleDocumentsLayerClick = useCallback((event) => {
        if (attachmentId) {
            return
        }
        const map = event.target
        const roadFeatures = map.queryRenderedFeatures({
            layers: [REFERENTIAL_LAYER],
        })
        const features = map.queryRenderedFeatures(event.point, {
            layers: [DOCUMENTS_LAYER],
        })
        if (!features.length) {
            return
        }
        const feature = pickTopFeature(features)
        const roadFeatureClicked = roadFeatures.find(roadFeature => {
            if (!roadFeature?.properties?.linearLocation || !feature?.properties?.linearLocation) {
                return false
            }

            const eventFeatureRoad = JSON.parse(feature.properties.linearLocation).road
            const roadFeatureRoad = JSON.parse(roadFeature.properties.linearLocation)[0].road
            return roadFeatureRoad === eventFeatureRoad
        })
        const linearLocation = feature?.properties?.linearLocation ? JSON.parse(feature.properties.linearLocation)[0] : null
        if (feature.properties.linearLocation) {
            setSelectedLinearLocations(({
                ...feature,
                properties: {
                    ...feature.properties,
                    linearLocation: JSON.stringify(linearLocation),
                },
            }))
        }
        if (roadFeatureClicked?.properties?.uuid) {
            setSegmentFeatureIdClicked(roadFeatureClicked?.properties?.uuid)
        }
        const signType = feature?.properties?.attached_object_content_type || feature.properties?.linear_location_type.toLowerCase()

        if (signType === NETWORK_ATTACHMENT && matchNetworkDetail) {
            return
        }

        storeMapBoundingBox()
        setImageFeatureClickedPosition(feature.geometry.coordinates)
        const location = ModulesInfo[signType].getDetailsRoute(feature?.properties)
        const newLocation = !linearLocation ? location
            : road ? location : `${linearLocation.road}/${location}`
        storeMapBoundingBox()
        setImageFeatureClickedPosition(feature.geometry.coordinates)
        return navigateWithSearchParams(newLocation, { paramsToDelete: [LINEAR_LOCATION_START, LINEAR_LOCATION_END, TYPE_ID] })
    }, [
        attachmentId,
        matchNetworkDetail,
        navigateWithSearchParams,
        road,
        setImageFeatureClickedPosition,
        setSegmentFeatureIdClicked,
        setSelectedLinearLocations,
        storeMapBoundingBox])

    return <DocumentsSource id={DOCUMENTS_SOURCE}>
        <Layer
            {...catalogItemsLayerConfig}
            id={DOCUMENTS_LAYER}
            onClick={handleDocumentsLayerClick}
        />
    </DocumentsSource>
}

DocumentsLayer.propTypes = {
    paramsToDeleteOnClick: PropTypes.arrayOf(PropTypes.string),
}

DocumentsLayer.defaultProps = {
    paramsToDeleteOnClick: [LINEAR_LOCATION_END, LINEAR_LOCATION_START, TYPE_ID],
}

function pickTopFeature(features) {
    if (features.length === 1) {
        return features[0]
    }
    const sortedFeatures = features.sort((a, b) => {
        const aIndex = a.properties.attached_object_content_type === NETWORK_ATTACHMENT ? NETWORK_ICON_INDEX : DEFAULT_INDEX
        const bIndex = b.properties.attached_object_content_type === NETWORK_ATTACHMENT ? NETWORK_ICON_INDEX : DEFAULT_INDEX
        if (aIndex !== bIndex) {
            return bIndex - aIndex
        }
        return 0
    })
    return sortedFeatures[0]
}